import { createSelector } from 'reselect';

/**
 * Selector to get externalAddOns data
 * @param {Object} state - redux state
 * @returns {Object} - external add-ons
 */
export const getExternalAddOns = state => state.externalAddOns;

/**
 * Selector to get large appliance attributes based on product id
 * @param {Object} param - large appliance attributes from product definition and product id 
 * @returns {Object} - large appliance attributes
 */
export const getLargeAppliancesAttributes = ({ largeAppliance, id: productId }) => createSelector(
  getExternalAddOns,
  (externalAddOnsFromState) => {
    const {
      productDeliveryDates,
      externalAddOns,
      earliestDeliveryDate,
      zipCode = null,
      error,
      errorMessage,
      inFlight,
    } = externalAddOnsFromState[productId] || {};

    const updatedLargeAppliance = { ...largeAppliance };

    updatedLargeAppliance.error = error;
    updatedLargeAppliance.errorMessage = errorMessage;
    updatedLargeAppliance.zipCode = zipCode === null ? updatedLargeAppliance.zipCode : zipCode;
    updatedLargeAppliance.externalAddOns = externalAddOns;
    updatedLargeAppliance.productDeliveryDates = productDeliveryDates;
    updatedLargeAppliance.earliestDeliveryDate = earliestDeliveryDate;
    updatedLargeAppliance.inFlight = inFlight;

    return updatedLargeAppliance;
  }
);

/**
 * Selector to get selected external add-ons
 * @param {String} productId - product id
 * @returns {String[]} - selected external add-ons
 */
export const getSelectedExternalAddOns = productId => createSelector(
  getExternalAddOns,
  (externalAddOns) => {
    const {
      externalAddOns:
            {
              addOns: {
                selection,
              },
            } = {},
    } = externalAddOns[productId] || {};

    return Object.keys(selection).filter(key => selection[key]);
  },
);

/**
 * Selector to get zipcode
 * @param {String} productId - product id
 * @returns {String} - zip code
 */
export const getExternalAddOnsZipCode = productId => createSelector(
  getExternalAddOns,
  (externalAddOns) => {
    const { zipCode } = externalAddOns[productId] || {};

    return zipCode;
  }
);

export const getExternalAddOnsForProduct = createSelector(
  getExternalAddOns,
  (externalAddOns = {}) => {
    return productId => {
      return externalAddOns[productId] || null;
    }
  }
);
